import React from 'react';
import { useCubeQuery } from '@cubejs-client/react';
import { PacmanLoader } from 'react-spinners';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import dayjs from 'dayjs';
import { Range } from 'react-date-range';
import MaterialTable from 'material-table';
import { tableIcons, tableLocalization } from 'src/theme/tableSettings';

interface ISampleByStaffComponentProps {
  dateRange: Range[];
  staff?: {
    id: string;
    family_name: string;
    given_name: string;
  }[];
  dateFormatter(item: string): string;
  colors: string[];
}
export const SamplesByStaffChart = (props: ISampleByStaffComponentProps) => {
  const { resultSet, isLoading, error, progress } = useCubeQuery({
    measures: ['samples.count'],
    timeDimensions: [
      {
        dimension: 'samples.createdAt',
        granularity: 'day',
        dateRange: [
          dayjs(props.dateRange[0].startDate?.toISOString()).format(
            'YYYY-MM-DD'
          ) || new Date(2021, 3, 1).toISOString(),
          dayjs(props.dateRange[0].endDate?.toISOString()).format(
            'YYYY-MM-DD'
          ) || new Date(2021, 3, 31).toISOString()
        ]
      }
    ],
    order: {
      'samples.count': 'desc'
    },
    dimensions: ['samples.staff']
  });
  if (isLoading) {
    return <PacmanLoader />;
  }
  return (
    <ResponsiveContainer height={300}>
      <BarChart data={resultSet?.chartPivot()} margin={{ bottom: 15, top: 10 }}>
        <XAxis
          dataKey="x"
          tickFormatter={props.dateFormatter}
          label={{
            value: 'Fecha',
            position: 'insideBottom',
            offset: -10
          }}
        />
        <YAxis
          label={{
            value: 'No. de Muestras',
            angle: -90,
            position: 'insideLeft',
            offset: 5
          }}
        />
        {resultSet?.seriesNames().map((series, i) => {
          const staffName = (props.staff &&
            props.staff.filter(
              staffer => staffer.id === series.yValues[0]
            )[0]) || { given_name: 'Desconocido', family_name: 'Desconocido' };
          return (
            <Bar
              key={series.key}
              stackId="a"
              dataKey={series.key}
              name={`${staffName?.given_name} ${staffName?.family_name}`}
              //   name={series.yValues[0]}
              fill={props.colors[i]}
            />
          );
        })}
        <Tooltip labelFormatter={props.dateFormatter} />
        <Legend verticalAlign="top" />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const SamplesByStaffTable = (props: ISampleByStaffComponentProps) => {
  const { resultSet, isLoading, error } = useCubeQuery({
    measures: ['samples.count'],
    timeDimensions: [
      {
        dimension: 'samples.createdAt',
        dateRange: [
          dayjs(props.dateRange[0].startDate?.toISOString()).format(
            'YYYY-MM-DD'
          ) || new Date(2021, 3, 1).toISOString(),
          dayjs(props.dateRange[0].endDate?.toISOString()).format(
            'YYYY-MM-DD'
          ) || new Date(2021, 3, 31).toISOString()
        ]
      }
    ],
    order: {
      'samples.count': 'desc'
    },
    dimensions: ['samples.staff']
  });

  if (isLoading) {
    return <PacmanLoader />;
  }

  if (error) {
    return <>Error procesando datos</>;
  }

  const data =
    resultSet &&
    resultSet.tablePivot().map(result => {
      const staffer = props?.staff?.filter(
        staffer => staffer.id === result['samples.staff']
      )[0] || { given_name: 'Desconocido', family_name: 'Desconocido' };
      return {
        staff: `${staffer.given_name} ${staffer.family_name}`,
        count: result['samples.count']
      };
    });

  return (
    data && (
      <MaterialTable
        icons={tableIcons}
        localization={tableLocalization}
        title="Muestras por Staff"
        data={data}
        columns={[
          { title: 'Staff', field: 'staff' },
          { title: 'Muestras', field: 'count' }
        ]}
        options={{
          exportButton: true
        }}
      />
    )
  );
};
